import React from 'react';
import { Dropdown, Avatar, Space, Select, Modal } from 'antd';
import { LogoutOutlined, DeleteOutlined, GlobalOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { secureLogger } from '../utils/secureLogger';

import { Locale } from '../locales';

interface UserMenuProps {
  user: {
    name: string;
    email: string;
    picture?: string;
  };
  locale: Locale;
  setLocale: (locale: Locale) => void;
  onDeleteUser: () => Promise<void>;
}

const UserMenu: React.FC<UserMenuProps> = ({
  user,
  locale,
  setLocale,
  onDeleteUser,
}) => {
  secureLogger.debug('User menu rendered');
  
  if (!user) {
    secureLogger.warn('No user provided to UserMenu');
    return null;
  }
  
  const items: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '8px 0', minWidth: '200px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <Avatar 
              src={user.picture} 
              size={40}
              style={{ marginRight: '12px' }}
            />
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                {user.name}
              </div>
              <div style={{ color: '#666', fontSize: '12px' }}>
                {user.email}
              </div>
            </div>
          </div>
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'language',
      label: (
        <div style={{ padding: '4px 0' }} onClick={(e) => e.stopPropagation()}>
          <Space>
            <GlobalOutlined />
            <span>言語 / Language</span>
          </Space>
          <div style={{ marginTop: '8px', marginLeft: '24px' }}>
            <Select
              value={locale}
              onChange={setLocale}
              style={{ width: '100%' }}
              size="small"
              onClick={(e) => e.stopPropagation()}
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
            >
              <Select.Option value="ja">日本語</Select.Option>
              <Select.Option value="en">English</Select.Option>
              <Select.Option value="zh">中文</Select.Option>
              <Select.Option value="es">Español</Select.Option>
            </Select>
          </div>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: (
        <Space>
          <LogoutOutlined />
          ログアウト
        </Space>
      ),
      onClick: async () => {
        try {
          const { signOutWithHostedUiRedirect } = await import('../auth/gateway');
          await signOutWithHostedUiRedirect();
        } catch (error) {
          secureLogger.error('Logout failed', { error: 'Sign out process failed' });
          window.location.reload();
        }
      },
    },
    {
      key: 'delete-user',
      danger: true,
      label: (
        <Space>
          <DeleteOutlined />
          ユーザー削除
        </Space>
      ),
      onClick: () => {
        Modal.confirm({
          title: 'ユーザーを削除',
          content: 'このユーザーを論理削除してログアウトします。データは保持されますが、再度利用するにはウェイトリスト申請と管理者承認が必要です。',
          okText: '削除する',
          okType: 'danger',
          cancelText: 'キャンセル',
          onOk: async () => onDeleteUser(),
        });
      },
    },
  ];

  return (
    <Dropdown
      menu={{ items }}
      placement="bottomRight"
      trigger={['click']}
      overlayStyle={{ minWidth: '220px' }}
    >
      <div
        style={{ cursor: 'pointer' }}
        onMouseEnter={(e) => {
          const avatar = e.currentTarget.querySelector('.ant-avatar') as HTMLElement;
          if (avatar) avatar.style.borderColor = '#1890ff';
        }}
        onMouseLeave={(e) => {
          const avatar = e.currentTarget.querySelector('.ant-avatar') as HTMLElement;
          if (avatar) avatar.style.borderColor = '#f0f0f0';
        }}
      >
        <Avatar 
          src={user.picture} 
          size={32}
          style={{ 
            border: '2px solid #f0f0f0',
            transition: 'border-color 0.2s'
          }}
        />
      </div>
    </Dropdown>
  );
};

export default UserMenu;
